import './style.scss'
import React, { useContext, useEffect, useState } from 'react'
import {
    FiChevronRight, FiArrowUp,
    FiHeadphones, FiHelpCircle, FiBookOpen, FiVideo, FiMessageSquare,
} from 'react-icons/fi'
import { Chart as ChartJS, registerables } from 'chart.js'
import { Line } from 'react-chartjs-2'
import { useNavigate } from 'react-router-dom'
import utility from 'src/utils/utility'
import {
    Box, Flex, Text, tokens,
    Card, Button,
} from 'src/componentv2'
import { fontMedium } from 'src/model/v2/interface/constants'
import { PAGE_PADDING_X } from 'src/model/v2/interface/constants'
import { useSidebarUser } from 'src/app/dashboardv2/hooks/useSidebarUser'
import CustomerApiContext, { CustomerMetrics, AuditLogSummary } from 'src/provider/API/v2/call-service/Customer/index'
import ConfigurationApiContext from 'src/provider/API/v2/call-service/configuration'
import { actionPillColors, formatAuditTimestamp } from 'src/app/dashboardv2/shared/auditFormatting'


ChartJS.register(...registerables)
const HARDCODED_TOTAL_TRANSACTIONS = 1450
const HARDCODED_FAILED_TRANSACTIONS = 394
const sparklineRed = [{ v: 20 }, { v: 28 }, { v: 22 }, { v: 35 }, { v: 60 }, { v: 45 }, { v: 55 }, { v: 38 }]
const donutData = [
    { name: 'Successful', value: 958, color: '#12B76A' },
    { name: 'Pending', value: 65, color: '#FDB022' },
    { name: 'Failed', value: 24, color: '#F04438' },
]

const resources = [
    { label: 'Contact support', Icon: FiHeadphones },
    { label: 'FAQs', Icon: FiHelpCircle },
    { label: 'Help Articles', Icon: FiBookOpen },
    { label: 'Video Guides', Icon: FiVideo },
    { label: 'Share Feedback', Icon: FiMessageSquare },
]

function getInitials(name: string) {
    const parts = name.trim().split(/\s+/)
    if (parts.length === 0 || !parts[0]) return '?'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[1][0]).toUpperCase()
}

function DonutChart() {
    const cx = 37.5
    const cy = 37.5
    const ringMidR = 30
    const ringWidth = 12
    const circumference = 2 * Math.PI * ringMidR
    const gap = 2
    const total = donutData.reduce((s, d) => s + d.value, 0)
    const usable = circumference - donutData.length * gap

    let cum = 0
    const arcs = donutData.map((seg) => {
        const arc = (seg.value / total) * usable
        const startOff = cum
        cum += arc + gap
        return { ...seg, arc, startOff }
    })

    return (
        <div className="home-dashboard__donut">
            <svg width={75} height={75} viewBox="0 0 75 75" style={{ display: 'block' }}>
                <circle cx={cx} cy={cy} r={ringMidR} fill="none" stroke="#EAECF0" strokeWidth={ringWidth} />
                {arcs.map((arc, i) => (
                    <circle
                        key={i}
                        cx={cx} cy={cy} r={ringMidR}
                        fill="none"
                        stroke={arc.color}
                        strokeWidth={ringWidth}
                        strokeDasharray={`${arc.arc} ${circumference - arc.arc}`}
                        strokeDashoffset={-arc.startOff}
                        transform={`rotate(-90 ${cx} ${cy})`}
                        strokeLinecap="butt"
                    />
                ))}
            </svg>
            <svg width={48} height={48} viewBox="0 0 48 48" className="home-dashboard__donut-hole">
                <circle cx={24} cy={24} r={23.5} fill="none" stroke="#EAECF0" strokeWidth={1} />
            </svg>
        </div>
    )
}

// ── Transactions card (hardcoded — no endpoint yet) ────────────────────
function TransactionsCard() {
    return (
        <Card padding="1.6rem" radius="1.2" borderColor={tokens.border} className="home-dashboard__stat-card">
            <Flex justify="space-between" items="center">
                <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>Total Transactions (Today)</Text>
                <FiChevronRight size={16} color={tokens.textMuted} />
            </Flex>

            <Flex
                items="flex-end" gap="2.4" wrap="wrap"
                style={{ background: tokens.pageBg, borderRadius: '0.8rem', padding: '1rem', marginTop: '1.6rem' }}
            >
                <Flex
                    direction="column" gap="1.2" items="flex-start"
                    style={{ minWidth: 0, flex: '1 1 16rem' }}
                >
                    <Text fontSize="2.4" style={{ ...fontMedium, lineHeight: 1.25 }} color={tokens.textDark}>
                        {HARDCODED_TOTAL_TRANSACTIONS.toLocaleString()}
                    </Text>
                    <Flex direction="column" gap="0.4" items="flex-start">
                        {donutData.map((d) => (
                            <Flex key={d.name} items="center" gap="0.6">
                                <Text fontSize="1.2" color={tokens.textMuted}>{d.name}</Text>
                                <Text fontSize="1.4" style={{ ...fontMedium, color: d.color }}>{d.value}</Text>
                            </Flex>
                        ))}
                    </Flex>
                </Flex>
                <DonutChart />
            </Flex>
        </Card>
    )
}

// ── Total Customers card — real, from admin/Customers/metrics ─────────
function TotalCustomersCard({ metrics }: { metrics: CustomerMetrics | null }) {
    return (
        <Card padding="1.6rem" radius="1.2" borderColor={tokens.border} className="home-dashboard__stat-card">
            <Flex justify="space-between" items="center">
                <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>Total Customers</Text>
                <FiChevronRight size={16} color={tokens.textMuted} />
            </Flex>

            <Box topMargin="1.6">
                <Text fontSize="2.4" style={{ ...fontMedium, lineHeight: 1.25 }} color={tokens.textDark}>
                    {metrics ? metrics.totalCustomers.toLocaleString() : '—'}
                </Text>
                <Flex items="center" gap="0.8" topMargin="0.6">
                    <FiArrowUp size={16} color="#12B76A" />
                    <Text fontSize="1.4" style={{ ...fontMedium, color: '#027A48' }}>
                        {metrics ? metrics.newCustomersThisWeek.toLocaleString() : '—'}
                    </Text>
                    <Text fontSize="1.4" color={tokens.textMuted}>new this week</Text>
                </Flex>
                <Text topMargin="0.6" fontSize="1.3" color={tokens.textMuted}>
                    {metrics ? `${metrics.totalExistingCustomers.toLocaleString()} existing customers` : ''}
                </Text>
            </Box>
        </Card>
    )
}

// ── New Customers card — real, from admin/Customers/metrics ───────────
function NewCustomersCard({ metrics }: { metrics: CustomerMetrics | null }) {
    return (
        <Card padding="1.6rem" borderColor={tokens.border} radius="1.2" className="home-dashboard__stat-card">
            <Flex justify="space-between" items="center">
                <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>New Customers (This Week)</Text>
                <FiChevronRight size={16} color={tokens.textMuted} />
            </Flex>

            <Box topMargin="1.6">
                <Text fontSize="2.4" style={{ ...fontMedium, lineHeight: 1.25 }} color={tokens.textDark}>
                    {metrics ? metrics.newCustomersThisWeek.toLocaleString() : '—'}
                </Text>
                <Flex items="center" gap="0.8" topMargin="0.6">
                    <Text fontSize="1.4" color={tokens.textMuted}>
                        {metrics ? `${metrics.totalNewCustomers.toLocaleString()} total new customers to date` : ''}
                    </Text>
                </Flex>
                <Text topMargin="0.6" fontSize="1.3" color={tokens.textMuted}>
                    {metrics ? `${metrics.existingCustomersThisWeek.toLocaleString()} existing customers active this week` : ''}
                </Text>
            </Box>
        </Card>
    )
}

// ── Failed Transactions card (hardcoded — no endpoint yet) ─────────────
function FailedTransactionsCard() {
    return (
        <Card padding="1.6rem" radius="1.2" borderColor={tokens.border} className="home-dashboard__stat-card">
            <Flex justify="space-between" items="center">
                <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>Failed Transactions</Text>
                <FiChevronRight size={16} color={tokens.textMuted} />
            </Flex>

            <Box topMargin="1.6">
                <Text fontSize="2.4" style={{ ...fontMedium, lineHeight: 1.25 }} color={tokens.textDark}>
                    {HARDCODED_FAILED_TRANSACTIONS.toLocaleString()}
                </Text>
                <Flex items="center" gap="0.8" topMargin="0.6">
                    <Text fontSize="1.4" style={{ ...fontMedium, color: '#F04438' }}>16%</Text>
                    <Text fontSize="1.4" color={tokens.textMuted}>Daily</Text>
                </Flex>

                <div className="home-dashboard__sparkline" style={{ height: 75 }}>
                    <Line
                        data={{
                            labels: sparklineRed.map((_, i) => i),
                            datasets: [
                                {
                                    data: sparklineRed,
                                    borderColor: '#F04438',
                                    borderWidth: 2,
                                    pointRadius: 0,
                                    fill: true,
                                    backgroundColor: (ctx: any) => {
                                        const { chart } = ctx
                                        const { ctx: canvasCtx, chartArea } = chart
                                        if (!chartArea) return 'rgba(240,68,56,0)'
                                        const gradient = canvasCtx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
                                        gradient.addColorStop(0, 'rgba(240,68,56,0.4)')
                                        gradient.addColorStop(1, 'rgba(240,68,56,0)')
                                        return gradient
                                    },
                                    tension: 0.4,
                                },
                            ],
                        }}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: { legend: { display: false }, tooltip: { enabled: false } },
                            scales: {
                                x: { display: false },
                                y: { display: false },
                            },
                        }}
                    />
                </div>
            </Box>
        </Card>
    )
}

// ── Recent activity — real, from admin/Institutions/AuditLogs ─────────
function RecentActivityCard({ entries, loading }: { entries: AuditLogSummary[]; loading: boolean }) {
    const navigate = useNavigate()

    return (
        <Card padding="0" radius="1.2" borderColor={tokens.border} style={{ overflow: 'hidden' }}>
            <Flex justify="space-between" items="center" style={{ padding: '1.2rem 1.6rem', borderBottom: `1px solid ${tokens.border}` }}>
                <Text fontSize="1.8" style={fontMedium} color={tokens.textDark}>Recent Activity</Text>
                <Button variant="outline" onClick={() => navigate('/dashboardv2/audit-trail')}>
                    View Audit Trail
                </Button>
            </Flex>

            <div className="home-dashboard__table-wrap">
                <table className="home-dashboard__table">
                    <thead>
                    <tr>
                        <th>Action</th>
                        <th>Performed By</th>
                        <th>Timestamp</th>
                    </tr>
                    </thead>
                    <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={3} style={{ textAlign: 'center', padding: '2.4rem' }}>
                                <Text fontSize="1.4" color={tokens.textMuted}>Loading recent activity...</Text>
                            </td>
                        </tr>
                    ) : entries.length === 0 ? (
                        <tr>
                            <td colSpan={3} style={{ textAlign: 'center', padding: '2.4rem' }}>
                                <Text fontSize="1.4" color={tokens.textMuted}>No recent activity yet.</Text>
                            </td>
                        </tr>
                    ) : (
                        entries.map((row) => {
                            const pill = actionPillColors(row.action)
                            return (
                                <tr key={row.id}>
                                    <td>
                                            <span
                                                className="home-dashboard__action-pill"
                                                style={{ background: pill.bg, color: pill.color }}
                                            >
                                                {row.action}
                                            </span>
                                    </td>
                                    <td>
                                        <Flex items="center" gap="1.2">
                                            <div className="home-dashboard__avatar-fallback">
                                                {getInitials(row.fullName || '?')}
                                            </div>
                                            <Flex direction="column" gap="0.1" items="flex-start">
                                                <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>{row.fullName}</Text>
                                                <Text fontSize="1.2" color={tokens.textMuted}>{row.role}</Text>
                                            </Flex>
                                        </Flex>
                                    </td>
                                    <td>
                                        <Text fontSize="1.4" color={tokens.textMuted}>{formatAuditTimestamp(row.performedAt)}</Text>
                                    </td>
                                </tr>
                            )
                        })
                    )}
                    </tbody>
                </table>
            </div>
        </Card>
    )
}

function ResourcesBar() {
    return (
        <Flex items="flex-start" gap="1.6" wrap="wrap">
            <Text fontSize="1.6" style={{ ...fontMedium, flexShrink: 0 }} color={tokens.textDark}>
                Resources
            </Text>
            <Flex gap="0.8" wrap="wrap">
                {resources.map(({ label, Icon }) => (
                    <button key={label} type="button" className="home-dashboard__resource-pill">
                        <Icon size={16} color={tokens.primary} />
                        {label}
                    </button>
                ))}
            </Flex>
        </Flex>
    )
}

const HomeDashboard = () => {
    const { name } = useSidebarUser()
    const firstName = name ? name.split(' ')[0] : ''
    const { getCustomerMetrics } = useContext(CustomerApiContext)
    const { getAuditLogs } = useContext(ConfigurationApiContext) || {}

    const [metrics, setMetrics] = useState<CustomerMetrics | null>(null)
    const [recentActivity, setRecentActivity] = useState<AuditLogSummary[]>([])
    const [loadingActivity, setLoadingActivity] = useState(true)

    useEffect(() => {
        getCustomerMetrics().then((m: CustomerMetrics | null) => {
            if (m) setMetrics(m)
        })
    }, [])

    useEffect(() => {
        if (typeof getAuditLogs !== 'function') {
            setLoadingActivity(false)
            return
        }

        getAuditLogs({ PageLimit: 5, PageNumber: 1 }).then(
            ({ items }: { items: AuditLogSummary[] }) => {
                setRecentActivity(items)
                setLoadingActivity(false)
            }
        )
    }, [])
    return (
        <div className="home-dashboard" style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
            <Text fontSize="2.4" style={fontMedium} color={tokens.textDark}>
                Welcome{firstName ? `, ${firstName}` : ''}!
            </Text>

            <div className="home-dashboard__stat-grid">
                <TransactionsCard />
                <TotalCustomersCard metrics={metrics} />
                <FailedTransactionsCard />
                <NewCustomersCard metrics={metrics} />
            </div>

            <Box topMargin="2.4">
                <RecentActivityCard entries={recentActivity} loading={loadingActivity} />
            </Box>

            <Box topMargin="2.4">
                <ResourcesBar />
            </Box>
        </div>
    )
}

export default utility.routeData(
    'home',
    'Home',
    <HomeDashboard />,
)